<template>
  <fn-grid class="birthday">
    <fn-grid-item columns="4">
      <fn-input
        id="forms-birthday-day"
        v-model:value="date.day"
        name="forms-birthday-day"
        inputmode="numeric"
        pattern="[0-9]*"
        :aria-label="$t('accessibility.form.inputLabels.birthdayDay')"
        :placeholder="$t('forms.birthday.day.placeholder')"
        :maxlength="2"
        :disabled="disabled"
        @update:value="goto($event, month)"
        @focus="$emit('focus', $event)"
        @blur="$emit('blur', $event)"
      />
    </fn-grid-item>
    <fn-grid-item columns="4">
      <fn-input
        id="forms-birthday-month"
        ref="month"
        v-model:value="date.month"
        name="forms-birthday-month"
        inputmode="numeric"
        :aria-label="$t('accessibility.form.inputLabels.birthdayMonth')"
        :placeholder="$t('forms.birthday.month.placeholder')"
        pattern="[0-9]*"
        :maxlength="2"
        :disabled="disabled"
        @update:value="goto($event, year)"
        @focus="$emit('focus', $event)"
        @blur="$emit('blur', $event)"
      />
    </fn-grid-item>
    <fn-grid-item columns="4">
      <fn-input
        id="forms-birthday-year"
        ref="year"
        v-model:value="date.year"
        name="forms-birthday-year"
        inputmode="numeric"
        :aria-label="$t('accessibility.form.inputLabels.birthdayYear')"
        :placeholder="$t('forms.birthday.year.placeholder')"
        pattern="[0-9]*"
        :maxlength="4"
        :disabled="disabled"
        @focus="$emit('focus', $event)"
        @blur="$emit('blur', $event)"
      />
    </fn-grid-item>
  </fn-grid>
</template>

<script setup lang="ts">
import { splitDate } from '@/utils/date';
import type { InputFieldProps } from '@/mixins/InputFieldAttributes';
import type { HTMLElementRef } from '@/types/forms';

defineProps<InputFieldProps>();
defineEmits(['update:value', 'focus', 'blur']);

const model = defineModel<string>('value');

const month = ref() as HTMLElementRef;
const year = ref() as HTMLElementRef;

const date = reactive<
  Partial<{
    day: string;
    month: string;
    year: string;
  }>
>({
  day: '',
  month: '',
  year: '',
});

const pad = (value: string | undefined, length: number, pad = '0') =>
  `${pad.repeat(length)}${value}`.slice(length * -1);

const dateFormatted = computed(() => {
  if (!date.year && !date.month && !date.day) {
    return '';
  }

  const formatted = {
    year: pad(date.year, 4),
    month: pad(date.month, 2),
    day: pad(date.day, 2),
  };

  return `${formatted.year}-${formatted.month}-${formatted.day}`;
});

const goto = (value: string, ref: any) => {
  if (value.length >= 2) {
    useFocus(ref).focused.value = true;
  }
};

const syncModelToComponent = () => {
  if (!model.value) {
    date.day = '';
    date.month = '';
    date.year = '';
    return;
  }

  // Transform date string `dd-mm-yyyy` to object `{ day: string; month: string; year: string }`
  const dateSplit = splitDate(model.value);

  if (!dateSplit?.year || !dateSplit?.month || !dateSplit?.day) {
    return;
  }

  date.day = dateSplit?.day?.toString();
  date.month = dateSplit?.month?.toString();
  date.year = dateSplit?.year?.toString();
};

const syncComponentToModel = () => {
  model.value = dateFormatted.value;
};

watch(dateFormatted, syncComponentToModel);
watch(() => model.value, syncModelToComponent, { immediate: true });
</script>
